import React, { useState, useEffect } from 'react';
import {
  Box, 
  Paper, 
  TextField, 
  Button, 
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar,
  FormHelperText,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ProductionQuantityLimits as ProductIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  Straighten as UnitIcon,
  Receipt as TaxIcon,
  QrCode as BarcodeIcon,
  AttachMoney as PriceIcon,
  Inventory as InventoryIcon,
  Inventory2 as QuantityIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import categoryService from '../../services/categoryService';
import unitService from '../../services/unitService';
import { getCurrentUser } from '../../services/authService';
import productService from '../../services/productService';

// Add a debugging function to help track what's going on
const logDebug = (message, data) => {
  console.log(`[ProductForm Debug] ${message}:`, data);
};

const ProductForm = ({ 
  initialData = null, 
  onSubmit, 
  onCancel, 
  isViewMode = false,
  // Accept pre-fetched data as props to avoid redundant API calls
  categoriesProp = null,
  unitsProp = null,
  taxesProp = null
}) => {
  const user = getCurrentUser();
  const companyId = user?.companyId;

  // Initialize form data with empty strings for all Select component values
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    description: '',
    categoryId: '',  // Empty string for Select
    unitId: '',      // Empty string for Select
    taxId: '',       // Empty string for Select
    purchasePrice: '',
    sellingPrice: '',
    mrp: '',
    hsnCode: '',
    quantity: '0',
    reorderLevel: '',
    imageFile: null,
    imagePreview: '',
    imageUrl: '',
    status: 'ACTIVE'
  });

  const [categories, setCategories] = useState(categoriesProp || []);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingTaxes, setLoadingTaxes] = useState(false);
  const [error, setError] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [units, setUnits] = useState(unitsProp || []);
  const [taxes, setTaxes] = useState(taxesProp || []);
  const [formErrors, setFormErrors] = useState({});

  // Update state if props change
  useEffect(() => {
    if (categoriesProp && categoriesProp.length > 0) {
      setCategories(categoriesProp);
    }
  }, [categoriesProp]);

  useEffect(() => {
    if (unitsProp && unitsProp.length > 0) {
      setUnits(unitsProp);
    }
  }, [unitsProp]);

  useEffect(() => {
    if (taxesProp && taxesProp.length > 0) {
      setTaxes(taxesProp);
    }
  }, [taxesProp]);

  // Fetch categories, units and taxes on component mount ONLY if not provided as props
  useEffect(() => {
    let isMounted = true;

    const fetchCategories = async () => {
      // Only fetch if not provided as props
      if (categoriesProp || categories.length > 0) {
        console.log("Categories already available, skipping fetch");
        return;
      }
      
      try {
        setLoadingCategories(true);
        const response = await categoryService.getAllCategories();
        console.log("Fetched Categories:", response); // Debugging
    
        if (isMounted) {
          if (response && Array.isArray(response.content) && response.content.length > 0) {
            // Store all categories in a flat array for selection
            setCategories(response.content);
            console.log("Fetched categories:", response.content.length); // Debugging
          } else {
            console.warn("No categories found");
            setCategories([]);
          }
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
        if (isMounted) {
          setError("Failed to load categories. Please try again later.");
        }
      } finally {
        if (isMounted) {
          setLoadingCategories(false);
        }
      }
    };
    
    const fetchUnits = async () => {
      // Only fetch if not provided as props
      if (unitsProp || units.length > 0) {
        console.log("Units already available, skipping fetch");
        return;
      }
      
      try {
        setLoadingUnits(true);
        const response = await unitService.getAllUnits();
        console.log("Fetched Units:", response); // Debugging
    
        if (isMounted) {
          if (response && Array.isArray(response.content) && response.content.length > 0) {
            setUnits(response.content); // Extract `content`
          } else {
            console.warn("No units found");
            setUnits([]);
          }
        }
      } catch (err) {
        console.error("Error fetching units:", err);
        if (isMounted) {
          setError("Failed to load units. Please try again later.");
        }
      } finally {
        if (isMounted) {
          setLoadingUnits(false);
        }
      }
    };
      
    const fetchTaxes = async () => {
      // Only fetch if not provided as props
      if (taxesProp || taxes.length > 0) {
        console.log("Taxes already available, skipping fetch");
        return;
      }
      
      try {
        setLoadingTaxes(true);
        const data = await productService.getTaxes();
        
        if (isMounted) {
          if (Array.isArray(data) && data.length > 0) {
            console.log('Successfully fetched taxes:', data);
            setTaxes(data);
          } else {
            console.warn('No taxes found');
            setTaxes([]);
          }
        }
      } catch (err) {
        console.error('Error fetching taxes:', err);
        if (isMounted) {
          setError('Failed to load taxes. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setLoadingTaxes(false);
        }
      }
    };

    fetchCategories();
    fetchUnits();
    fetchTaxes();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (initialData) {
      console.log('Initializing product form with data:', initialData);
      console.log('Category data:', {
        category: initialData.category,
        categoryId: initialData.categoryId,
        categoryFromObj: initialData.category?.id
      });
      console.log('Unit data:', {
        unit: initialData.unit,
        unitId: initialData.unitId,
        unitFromObj: initialData.unit?.id
      });
      console.log('Tax data:', {
        tax: initialData.tax,
        taxId: initialData.taxId,
        taxFromObj: initialData.tax?.id
      });
    }
  }, [initialData]);

  // In your second useEffect, wait until everything is loaded before setting the form data
  useEffect(() => {
    if (initialData && categories.length > 0 && units.length > 0) {
      try {
        logDebug('Setting form data with loaded options', { initialData, categories, units, taxes });
        
        // Extract category ID with proper string conversion
        let categoryId = '';
        if (initialData.categoryId !== undefined && initialData.categoryId !== null) {
          categoryId = String(initialData.categoryId);
        } else if (initialData.category?.id !== undefined && initialData.category?.id !== null) {
          categoryId = String(initialData.category.id);
        }
        
        // Extract unit ID with proper string conversion
        let unitId = '';
        if (initialData.unitId !== undefined && initialData.unitId !== null) {
          unitId = String(initialData.unitId);
        } else if (initialData.unit?.id !== undefined && initialData.unit?.id !== null) {
          unitId = String(initialData.unit.id);
        }
        
        // Extract tax ID with proper string conversion
        let taxId = '';
        if (initialData.taxId !== undefined && initialData.taxId !== null) {
          taxId = String(initialData.taxId);
        } else if (initialData.tax?.id !== undefined && initialData.tax?.id !== null) {
          taxId = String(initialData.tax.id);
        }
        
        // Convert option IDs to strings for safer comparison
        const categoryIds = categories.map(c => String(c.id));
        const unitIds = units.map(u => String(u.id));
        const taxIds = taxes.map(t => String(t.id));
        
        logDebug('Extracted IDs', { categoryId, unitId, taxId });
        logDebug('Available IDs', { categoryIds, unitIds, taxIds });
        
        // Ensure values are in the available options
        const validCategoryId = categoryIds.includes(categoryId) ? categoryId : '';
        const validUnitId = unitIds.includes(unitId) ? unitId : '';
        const validTaxId = taxIds.includes(taxId) ? taxId : '';
        
        logDebug('Valid IDs after verification', { validCategoryId, validUnitId, validTaxId });
        
        // Set form data with validated values
        setFormData({
          name: initialData.name || '',
          sku: initialData.sku || '',
          description: initialData.description || '',
          categoryId: validCategoryId,
          unitId: validUnitId,
          taxId: validTaxId,
          purchasePrice: initialData.purchasePrice || '',
          sellingPrice: initialData.sellingPrice || '',
          mrp: initialData.mrp || '',
          hsnCode: initialData.hsnCode || '',
          quantity: initialData.quantity || '0',
          reorderLevel: initialData.reorderLevel || '',
          imageFile: null,
          imagePreview: '',
          imageUrl: initialData.imageUrl || '',
          status: initialData.status || 'ACTIVE'
        });
        
        // Set image preview if available
        if (initialData.imageUrl) {
          setFormData(prev => ({
            ...prev,
            imagePreview: productService.getProductImageUrl(initialData.imageUrl)
          }));
        }
      } catch (err) {
        console.error('Error setting form data:', err);
      }
    }
  }, [initialData, categories, units, taxes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log('Form field changed:', name, value); // Debug log
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear any errors for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPEG, PNG, or GIF)');
      return;
    }

    // Validate file size (max 3MB)
    if (file.size > 3 * 1024 * 1024) {
      setError('Image size should not exceed 3MB');
      return;
    }

    // Preview the image
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        imageFile: file,
        imagePreview: reader.result
      }));
    };
    reader.readAsDataURL(file);

    // Upload the image
    try {
      setUploadLoading(true);
      setError(null);
      const imageUrl = await productService.uploadProductImage(file);
      console.log('Image uploaded successfully:', imageUrl);
      setFormData(prev => ({
        ...prev,
        imageUrl
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      imageFile: null,
      imagePreview: '',
      imageUrl: ''
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name?.trim()) {
      errors.name = 'Product name is required';
    }

    // SKU: if empty, backend will auto-generate from company settings (prefix + pattern)
    // No frontend auto-generation needed

    if (!formData.categoryId) {
      errors.categoryId = 'Category is required';
    }

    if (!formData.unitId) {
      errors.unitId = 'Unit of measurement is required';
    }

    if (!formData.purchasePrice || parseFloat(formData.purchasePrice) < 0) {
      errors.purchasePrice = 'Valid purchase price is required';
    }

    if (!formData.sellingPrice || parseFloat(formData.sellingPrice) <= 0) {
      errors.sellingPrice = 'Valid selling price is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Prepare the data
      const submitData = {
        ...formData,
        companyId: parseInt(companyId) || 1,
        categoryId: parseInt(formData.categoryId),
        unitId: parseInt(formData.unitId),
        taxId: formData.taxId ? parseInt(formData.taxId) : null,
        purchasePrice: parseFloat(formData.purchasePrice) || 0,
        sellingPrice: parseFloat(formData.sellingPrice) || 0,
        mrp: formData.mrp ? parseFloat(formData.mrp) : null,
        hsnCode: formData.hsnCode || null,
        quantity: parseInt(formData.quantity || '0'),
        reorderLevel: formData.reorderLevel ? parseInt(formData.reorderLevel) : null,
        status: formData.status || 'ACTIVE',
        imageUrl: formData.imageUrl
      };

      // Remove unnecessary fields
      delete submitData.imageFile;
      delete submitData.imagePreview;

      console.log('Submitting product data:', submitData);
      await onSubmit(submitData);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.response?.data?.message || 'Failed to save product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseError = () => {
    setError(null);
  };

  // Format categories for the dropdown to show hierarchy
  const getFormattedCategories = () => {
    // First get all parent categories
    const parentCategories = categories.filter(c => !c.parentCategory);
    
    // Then get subcategories
    const formattedOptions = [];
    
    // Add all parent categories first
    parentCategories.forEach(parent => {
      formattedOptions.push({
        id: parent.id,
        name: parent.name,
        displayName: parent.name,
        isParent: true
      });
      
      // Find all children of this parent
      const children = categories.filter(c => 
        c.parentCategory && c.parentCategory.id === parent.id
      );
      
      // Add children with indentation marker
      children.forEach(child => {
        formattedOptions.push({
          id: child.id,
          name: child.name,
          displayName: `↳ ${child.name}`,
          isParent: false
        });
      });
    });
    
    // Add any remaining categories that don't have parent/child relationship
    const remainingCategories = categories.filter(c => 
      !parentCategories.some(p => p.id === c.id) && 
      !c.parentCategory
    );
    
    remainingCategories.forEach(cat => {
      formattedOptions.push({
        id: cat.id,
        name: cat.name,
        displayName: cat.name,
        isParent: false
      });
    });
    
    return formattedOptions;
  };

  // Get clean category name for display after selection
  const getSelectedCategoryName = (selectedId) => {
    const category = categories.find(c => String(c.id) === String(selectedId));
    return category ? category.name : '';
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, mb: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Product Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Product Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ProductIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  multiline
                  rows={4}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DescriptionIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!formErrors.categoryId}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleChange}
                    disabled={isViewMode || loading || loadingCategories}
                    startAdornment={
                      <InputAdornment position="start">
                        {loadingCategories ? <CircularProgress size={20} /> : <CategoryIcon />}
                      </InputAdornment>
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          overflowY: 'auto'
                        }
                      }
                    }}
                    renderValue={(selected) => getSelectedCategoryName(selected)}
                  >
                    <MenuItem value="">
                      <em>Select a category</em>
                    </MenuItem>
                    {getFormattedCategories().map((category) => (
                      <MenuItem 
                        key={category.id} 
                        value={String(category.id)}
                        sx={category.isParent ? { fontWeight: 'bold' } : { ml: 2 }}
                      >
                        {category.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.categoryId && <FormHelperText>{formErrors.categoryId}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!formErrors.unitId}>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    name="unitId"
                    value={formData.unitId}
                    onChange={handleChange}
                    disabled={isViewMode || loading || loadingUnits}
                    startAdornment={
                      <InputAdornment position="start">
                        {loadingUnits ? <CircularProgress size={20} /> : <UnitIcon />}
                      </InputAdornment>
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          overflowY: 'auto'
                        }
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a unit</em>
                    </MenuItem>
                    {units.map((unit) => (
                      <MenuItem key={unit.id} value={String(unit.id)}>
                        {unit.name} ({unit.abbreviation})
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.unitId && <FormHelperText>{formErrors.unitId}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!formErrors.taxId}>
                  <InputLabel>Tax</InputLabel>
                  <Select
                    name="taxId"
                    value={formData.taxId || ''}
                    onChange={handleChange}
                    disabled={isViewMode || loading || loadingTaxes}
                    startAdornment={
                      <InputAdornment position="start">
                        {loadingTaxes ? <CircularProgress size={20} /> : <TaxIcon />}
                      </InputAdornment>
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          overflowY: 'auto'
                        }
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>No Tax</em>
                    </MenuItem>
                    {taxes.map((tax) => (
                      <MenuItem key={tax.id} value={String(tax.id)}>
                        {tax.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.taxId && <FormHelperText>{formErrors.taxId}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SKU"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BarcodeIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="HSN Code"
                  name="hsnCode"
                  value={formData.hsnCode}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BarcodeIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Pricing & Inventory
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Purchase Price"
                  name="purchasePrice"
                  type="number"
                  value={formData.purchasePrice}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  error={!!formErrors.purchasePrice}
                  helperText={formErrors.purchasePrice}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PriceIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="MRP (Maximum Retail Price)"
                  name="mrp"
                  type="number"
                  value={formData.mrp}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    endAdornment: <InputAdornment position="end"><PriceIcon /></InputAdornment>
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Selling Price"
                  name="sellingPrice"
                  type="number"
                  value={formData.sellingPrice}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  required
                  error={!!formErrors.sellingPrice}
                  helperText={formErrors.sellingPrice}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    endAdornment: <InputAdornment position="end"><PriceIcon /></InputAdornment>
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  name="quantity"
                  type="number"
                  value={formData.quantity}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <QuantityIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Reorder Level"
                  name="reorderLevel"
                  type="number"
                  value={formData.reorderLevel}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  InputProps={{
                    inputProps: { min: 0 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <InventoryIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status || 'ACTIVE'}
                    onChange={handleChange}
                    disabled={isViewMode || loading}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          overflowY: 'auto'
                        }
                      }
                    }}
                  >
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Right Column - Image upload */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Product Image
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 2
              }}
            >
              {formData.imagePreview ? (
                <Box
                  component="img"
                  src={formData.imagePreview}
                  alt="Product preview"
                  sx={{
                    width: '100%',
                    maxHeight: 200,
                    objectFit: 'contain',
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'action.hover',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  <ImageIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                </Box>
              )}
              
              {!isViewMode && (
                <Box sx={{ display: 'flex', width: '100%', gap: 1 }}>
                  <Button
                    variant="contained"
                    component="label"
                    disabled={loading || uploadLoading}
                    startIcon={uploadLoading ? <CircularProgress size={20} /> : <ImageIcon />}
                    sx={{ flexGrow: 1 }}
                  >
                    {uploadLoading ? 'Uploading...' : 'Upload Image'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </Button>
                  {formData.imagePreview && (
                    <IconButton 
                      onClick={handleRemoveImage}
                      color="error"
                      disabled={loading || uploadLoading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              )}
            </Box>
            
            <Typography variant="caption" color="text.secondary">
              Upload a high-quality image of the product. Recommended size: 800x800 pixels. Maximum file size: 3MB.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      
      {!isViewMode && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ mr: 1 }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {loading ? 'Saving...' : (initialData ? 'Update Product' : 'Create Product')}
          </Button>
        </Box>
      )}

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductForm; 
